import { TabController, TABS } from "./ui/tab-controller";
import Badge from "@/shared/data-display/badge";
import Button from "@/shared/form/button";
import { CheckOutlined } from "@ant-design/icons";
import Tabs from "@/shared/data-display/tabs";
import { TabTitle } from "@/modules/common/widgets/notification-bell/ui/tab-title";
import InfiniteScroll from "react-infinite-scroll-component";
import { List } from "@/shared/data-display/list";
import { NotificationListItemContainer } from "@/modules/common/widgets/notification-bell/ui/notification-list-item-container";
import { Footer } from "@/modules/common/widgets/notification-bell/ui/footer";
import { FooterLink } from "@/modules/common/widgets/notification-bell/ui/footer-link";
import { NOTIFICATIONS_PATH } from "../../pages/notifications";
import { InfiniteScrollContainer } from "./ui/infinite-scroll-container";
import { NotificationListItem } from "@/modules/common/widgets/notification-bell/ui/notification-list-item";
import { NotificationListItemTask } from "./views/notification-list-item-task/notification-list-item-task";
import { useTasksNotification } from "@/modules/common/widgets/notification-bell/model/use-tasks-notification";
import { useNotifications } from "@/modules/common/widgets/notification-bell/model/use-notifications";
import { useTranslations } from "use-intl";
import { useReadAllNotificationsMutation } from "@/modules/common/widgets/notification-bell/model/use-read-all-notifications-mutation";
import { useReadNotification } from "@/modules/common/features/read-notification";
import Text from "@/shared/typography/text";
import { TASKS_LIST_PATH } from "@/modules/tasks/pages/tasks";

export function NotificationBellContent({
  allNumber,
  primaryColor,
  unreadMessageNumber,
  uncompletedTasksNumber,
}: {
  allNumber: number;
  primaryColor: string;
  unreadMessageNumber: number;
  uncompletedTasksNumber: number;
}) {
  const t = useTranslations("NotificationBell");

  const {
    tasks,
    loading: taskLoading,
    loadMore: loadMoreTasks,
    total: totalTasks,
  } = useTasksNotification();

  const {
    notifications,
    loading: notificationLoading,
    loadMore,
    total,
  } = useNotifications();

  const { readAll, loading } = useReadAllNotificationsMutation();
  const { read } = useReadNotification();

  return (
    <div className={"w-[300px] xl:w-[400px]"}>
      <TabController>
        {({ tab, setTab }) => (
          <div>
            <div className={"px-4 xl:px-6 py-4"}>
              <div className={"flex items-center justify-between"}>
                <div className={"flex items-center gap-1"}>
                  <Text bold>{t("Alerts")}</Text>
                  {!!allNumber && (
                    <Badge count={allNumber} color={primaryColor} />
                  )}
                </div>
                {tab === TABS.notification && (
                  <div>
                    <Button
                      type={"link"}
                      className={"!p-0 underline"}
                      icon={<CheckOutlined />}
                      size={"small"}
                      onClick={() => readAll()}
                      loading={loading}
                    >
                      {t("Mark all as read")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <Tabs
                activeKey={tab}
                onChange={setTab}
                size={"small"}
                destroyInactiveTabPane
                className={
                  "[&_.ant-tabs-nav]:!mb-0 [&_.ant-tabs-nav-wrap]:!mx-4 [&_.ant-tabs-nav-wrap]:xl:!mx-6"
                }
                items={[
                  {
                    key: TABS.notification,
                    label: (
                      <TabTitle
                        title={t("Notifications")}
                        count={unreadMessageNumber}
                        active={TABS.notification === tab}
                      />
                    ),
                    children: (
                      <div>
                        <InfiniteScrollContainer
                          id={"notifications-scrollable-container"}
                        >
                          <InfiniteScroll
                            next={loadMore}
                            hasMore={total > notifications.length}
                            loader={null}
                            dataLength={notifications.length}
                            scrollableTarget={
                              "notifications-scrollable-container"
                            }
                          >
                            <List
                              loading={notificationLoading}
                              dataSource={notifications}
                              renderItem={(item) => (
                                <List.Item className={"!p-0 !block"}>
                                  <NotificationListItemContainer
                                    read={item.wasRead}
                                  >
                                    <NotificationListItem
                                      notification={item}
                                      read={read}
                                    />
                                  </NotificationListItemContainer>
                                </List.Item>
                              )}
                            />
                          </InfiniteScroll>
                        </InfiniteScrollContainer>
                        <Footer>
                          <FooterLink to={NOTIFICATIONS_PATH}>
                            {t("View all notifications")}
                          </FooterLink>
                        </Footer>
                      </div>
                    ),
                  },
                  {
                    key: TABS.task,
                    label: (
                      <TabTitle
                        title={t("Tasks")}
                        count={uncompletedTasksNumber}
                        active={TABS.task === tab}
                      />
                    ),
                    children: (
                      <div>
                        <InfiniteScrollContainer
                          id={"tasks-scrollable-container"}
                        >
                          <InfiniteScroll
                            next={loadMoreTasks}
                            hasMore={totalTasks > tasks.length}
                            loader={null}
                            dataLength={tasks.length}
                            scrollableTarget={"tasks-scrollable-container"}
                          >
                            <List
                              loading={taskLoading}
                              dataSource={tasks}
                              renderItem={(item) => (
                                <List.Item className={"!p-0 !block"}>
                                  <NotificationListItemContainer>
                                    <NotificationListItemTask task={item} />
                                  </NotificationListItemContainer>
                                </List.Item>
                              )}
                            />
                          </InfiniteScroll>
                        </InfiniteScrollContainer>
                        <Footer>
                          <FooterLink to={TASKS_LIST_PATH}>
                            {t("View all tasks")}
                          </FooterLink>
                        </Footer>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        )}
      </TabController>
    </div>
  );
}
